import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ZoneInfo, Coordinates, Zone, TriggerCoordinates } from '@interfaces/rest/interfaces/zone';
// import { RadioModal } from '@components/radio-modal/radio-modal.component';

import { User, UserSocket } from '@interfaces/user';
import { TranslateService } from '@ngx-translate/core';
import { ProvinceService } from '@services/province.service';
import { MapConfiguration } from '@static_data/maps';
import { Subject, takeUntil } from 'rxjs';


@Component({
  selector: 'app-google-map-general',
  templateUrl: './google-map-general.component.html',
  styleUrls: ['./google-map-general.component.scss'],
})
export class GoogleMapGeneralComponent implements OnInit, OnChanges {
  @Input() user?: User;
  @Input() searchParams?: { zone: string, status: string, searchValue: string };
  selectedFilter = 'all';

  centerMap?: Coordinates;

  firstTime = true;

  @ViewChild('map') mapElem?: any;

  // Map
  map?: google.maps.Map;
  driversMarkers: Map<number, google.maps.Marker> = new Map();
  driverMarker?: google.maps.Marker;
  zonesDraws: google.maps.Polygon[] = [];

  availableZones: Zone[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private province: ProvinceService
  ) {
    this.province.zoneInfo$.pipe(
      takeUntil(this.destroy$))
      .subscribe(async zoneInfo => {
        if (zoneInfo) {
          setTimeout(() => {
            this.checkEnableZones(zoneInfo);
            this.drawDrivers();
          }, 100);
        }
      })
  }

  async ngOnInit() {
    if (!this.centerMap) {
      this.centerMap = await this.province.getCenterMap();
    }
    this.loadMap();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Si cambian los searchParams y ya tenemos el mapa cargado, redibujamos los conductores
    if (changes['searchParams'] && !changes['searchParams'].firstChange && this.map) {
      this.drawDrivers();
    }
  }

  async ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkEnableZones(zoneInfo: ZoneInfo) {
    if (!this.user?.role?.includes('admin')) {
      this.availableZones = zoneInfo.zones.filter(elem => elem?.information);
      if (this.zonesDraws.length === 0) {
        this.setZones();
      }
    } else {
      this.availableZones = zoneInfo.zones;
      if (this.zonesDraws.length === 0) {
        this.setZones();
      }
    }
  }

  drawZones(coordinates: Coordinates[], index: number) {
    const colors = [
      '#6ca974', '#150b9e', '#158803', '#cd574e', '#3d4056',
      '#e85186', '#7c25bc', '#d92354', '#ff0640', '#2bbaad',
      '#787291', '#2d1b1c', '#ee8abb', '#ded547', '#a7d3e2'
    ];
    var polygon = new google.maps.Polygon({
      paths: coordinates,
      strokeColor: colors[index],
      strokeOpacity: 1,
      strokeWeight: 1,
      fillColor: '#000000',
      fillOpacity: 0
    });

    // Add the polygon to the map
    polygon.setMap(this.map!);
    this.zonesDraws.push(polygon);
  }

  setZones() {
    if (this.map) {
      let index = 0;
      this.availableZones.forEach(zone => {
        if (zone?.coordinates) {
          this.drawZones(zone.coordinates, index);
          index += 1;
          if (zone.triggerCoordinates) {
            const coordinates = this.getTriggerTime(zone.triggerCoordinates)
            if (coordinates) {
              this.drawZones(this.getTriggerPoligon(coordinates), index);
              index += 1;
            }
          }
        }
      });
    }
  }

  getTriggerTime(triggerCoordinates: TriggerCoordinates[]) {
    let coordinates: Coordinates | undefined = undefined;
    triggerCoordinates.forEach(triggerPoint => {
      const triggerTime = triggerPoint.time;
      const [startTime, endTime] = triggerTime.split('_');

      const now = new Date();
      const startDate = new Date(now);
      const endDate = new Date(now);

      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);
      startDate.setHours(startHours, startMinutes, 0, 0);
      endDate.setHours(endHours, endMinutes, 0, 0);

      if (now >= startDate && now <= endDate) {
        coordinates = triggerPoint.coordinates;
      }
    });
    return coordinates;
  }

  getTriggerPoligon(center: Coordinates, distance = 50): Coordinates[] {
    // Convertir distancia de metros a grados aproximados
    let latDistance = distance / 111320;
    let lngDistance = distance / (111320 * Math.cos(center.lat * (Math.PI / 180)));

    // Calcular las esquinas del cuadrado
    let topLeft = { lat: center.lat + latDistance, lng: center.lng - lngDistance };
    let topRight = { lat: center.lat + latDistance, lng: center.lng + lngDistance };
    let bottomRight = { lat: center.lat - latDistance, lng: center.lng + lngDistance };
    let bottomLeft = { lat: center.lat - latDistance, lng: center.lng - lngDistance };

    // Devolver el polígono como un arreglo de puntos
    return [topLeft, topRight, bottomRight, bottomLeft, topLeft]; // Cerramos el polígono añadiendo el punto inicial al final
  }

  loadMap() {
    const mapOptions = MapConfiguration;
    setTimeout(() => {
      this.map = new google.maps.Map(this.mapElem.nativeElement, {
        center: { lat: this.centerMap!.lat, lng: this.centerMap!.lng },
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        clickableIcons: false,
        disableDefaultUI: true,
        ...mapOptions,
      });
    }, 0);

  }

  async drawDrivers() {
    let drivers: UserSocket[] | undefined = await this.province.getDrivers();
    if (!this.map || !drivers) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    const visibleDrivers: UserSocket[] = [];

    const newIds = new Set(drivers.map(driver => driver.d));

    drivers.forEach(driver => {
      // Filtrar por status si existe en searchParams
      const matchesStatus = !this.searchParams?.status || driver.s === this.searchParams.status;

      // Filtrar por taxi_number si existe searchValue
      const taxiNumber = driver.license?.taxiNumber?.toString() || '';
      const matchesTaxiNumber = !this.searchParams?.searchValue ||
        taxiNumber.toLowerCase() === this.searchParams.searchValue.toLowerCase();

      // Solo dibujar el conductor si cumple ambos filtros
      if (matchesStatus && matchesTaxiNumber) {
        this.createOrUpdateMarker(driver);
        visibleDrivers.push(driver);

        if (this.firstTime) {
          const position = new google.maps.LatLng(driver.c?.[0], driver.c?.[1]);
          bounds.extend(position);
        }
      }
    });

    this.driversMarkers.forEach((marker, id) => {
      const driver = drivers?.find(driver => driver.d === id);
      if (!driver) {
        marker.setMap(null);
        this.driversMarkers.delete(id);
        return;
      }

      // Aplicar los mismos filtros para remover marcadores
      const matchesStatus = !this.searchParams?.status || driver.s === this.searchParams.status;
      const taxiNumber = driver.license?.taxiNumber?.toString() || '';
      const matchesTaxiNumber = !this.searchParams?.searchValue ||
        taxiNumber.toLowerCase() === this.searchParams.searchValue.toLowerCase();

      if (!matchesStatus || !matchesTaxiNumber || !newIds.has(id)) {
        marker.setMap(null);
        this.driversMarkers.delete(id);
      }
    });

    if (this.firstTime && visibleDrivers.length > 0) {
      this.adjustMapView(visibleDrivers, bounds);
      this.firstTime = false;
    }
  }

  private adjustMapView(visibleDrivers: UserSocket[], bounds: google.maps.LatLngBounds): void {
    if (!this.map) return;

    const driverCount = visibleDrivers.length;

    if (driverCount === 1) {
      // Para un solo taxista, centrar en su posición con zoom moderado
      const driver = visibleDrivers[0];
      const center = new google.maps.LatLng(driver.c?.[0], driver.c?.[1]);
      this.map.setCenter(center);
      this.map.setZoom(15); // Zoom moderado para un solo taxista
    } else if (driverCount === 2) {
      // Para dos taxistas, usar un zoom un poco menor
      if (!isNaN(bounds.getCenter().lat())) {
        this.map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });

        // Asegurar que el zoom no sea excesivo
        setTimeout(() => {
          if (this.map && this.map.getZoom() && this.map.getZoom()! > 16) {
            this.map.setZoom(16);
          }
        }, 100);
      }
    } else {
      // Para 3 o más taxistas, usar fitBounds con padding
      if (!isNaN(bounds.getCenter().lat())) {
        this.map.fitBounds(bounds, { top: 80, bottom: 80, left: 80, right: 80 });

        // Asegurar que el zoom no sea ni muy alto ni muy bajo
        setTimeout(() => {
          if (this.map && this.map.getZoom()) {
            const currentZoom = this.map.getZoom()!;
            if (currentZoom > 17) {
              this.map.setZoom(17);
            } else if (currentZoom < 10) {
              this.map.setZoom(10);
            }
          }
        }, 100);
      }
    }
  }

  private createOrUpdateMarker(driver: UserSocket): void {
    if (!driver.d) {
      return;
    }
    let marker = this.driversMarkers.get(driver.d);

    // console.error('driver', driver);
    const labelContentActive: google.maps.MarkerLabel = {
      text: driver.license?.taxiNumber?.toString() || '-',
      className: 'custom-marker-label active'
    };
    const labelContentOccupied: google.maps.MarkerLabel = {
      text: driver.license?.taxiNumber?.toString() || '-',
      className: 'custom-marker-label occupied'
    };

    if (marker) {
      // update existing marker
      marker.setPosition(new google.maps.LatLng(driver.c[0], driver.c[1]));
      marker.setLabel(driver.s === 'active' ? labelContentActive : labelContentOccupied);
    } else {
      // Create new marker
      marker = new google.maps.Marker({
        map: this.map,
        label: driver.s === 'active' ? labelContentActive : labelContentOccupied,
        position: new google.maps.LatLng(driver.c[0], driver.c[1]),
        icon: this.getIconForStatus()
      });

      const extra = driver.license?.extra?.map(elem => this.translate.instant('services.' + elem))?.join(', ') || '';
      const name = driver.license?.drivers?.find(driverObj => driverObj?.id === driver.d)?.name || '';
      const content = `Nombre: ${name}<br>Matricula: ${driver.license?.registrationNumber}<br>Coche: ${driver.license?.brand} ${driver.license?.model}<br>Extras: ${extra}`;
      const infoWindow = new google.maps.InfoWindow({ content });

      marker.addListener('click', () => {
        infoWindow.open({
          anchor: marker,
          map: this.map,
          shouldFocus: false,
        });
      });

      this.driversMarkers.set(driver.d, marker);
    }

  }

  private getIconForStatus(): string {
    return '/assets/icons/pin.png';
  }
}
